import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Tooltip, Avatar, Divider,
  Tab, Tabs,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { useLocation, useNavigate, Navigate, useMatch } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getApplicationsByOwner } from '../../services/api';
import logoImg from '../../Assets/images/yum-logo.svg';

function getInitials(name) {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.isProductManager ?? false;

  // For admins: track whether the applications API returned any apps
  const [hasApps, setHasApps] = useState(null); // null = loading, true/false = resolved

  useEffect(() => {
    if (!isAdmin) return;
    getApplicationsByOwner(user.userId)
      .then((apps) => setHasApps(apps.length > 0))
      .catch(() => setHasApps(false));
  }, [isAdmin, user?.userId]);

  // For admins: wait until apps check resolves before rendering
  const appsCheckPending = isAdmin && hasApps === null;

  // Admin with no apps → redirect /home to /admin-dashboard
  const shouldRedirectToAdmin = isAdmin && hasApps === false && location.pathname === '/home';

  // Show both tabs only when admin has apps; otherwise only Admin Dashboard tab
  const showBothTabs = isAdmin && hasApps === true;

  // Active tab — useMatch is more reliable than manual string comparison
  // (handles trailing slashes, case, etc.)
  const isAdminRoute = useMatch('/admin-dashboard');
  const activeTab = isAdminRoute ? '/admin-dashboard' : '/home';

  if (appsCheckPending) return null; // wait silently — avoids flash of wrong tab
  if (shouldRedirectToAdmin) return <Navigate to="/admin-dashboard" replace />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#0A1929', borderBottom: 'none' }}>
        <Toolbar sx={{ minHeight: '56px !important', px: { xs: 2, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5 }}>
            <img src={logoImg} alt="Logo" height={40} />
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 1.5, borderColor: 'rgba(255,255,255,0.15)' }} />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
            Access Review Portal
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3 }}>
                {user?.userId}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>
                {user?.name}
                {isAdmin && (
                  <Box component="span" sx={{ ml: 0.75, color: '#42A5F5', fontWeight: 700 }}>
                    · Admin
                  </Box>
                )}
              </Typography>
            </Box>
            <Avatar
              sx={{
                width: 34, height: 34,
                bgcolor: '#1976D2',
                fontSize: '0.75rem',
                fontWeight: 700,
              }}
            >
              {getInitials(user?.name || '')}
            </Avatar>
            <Tooltip title="Sign Out">
              <IconButton
                onClick={logout}
                size="small"
                sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>

        {/* ── PM Navigation tabs (only for product managers) ─────────────── */}
        {isAdmin && (
          <Box sx={{ bgcolor: '#060F1A', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 4 } }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => navigate(v)}
                textColor="inherit"
                TabIndicatorProps={{ style: { backgroundColor: '#1976D2', height: 3 } }}
                sx={{ minHeight: 42 }}
              >
                {/* Show Access Review tab only when admin has apps */}
                {showBothTabs && (
                  <Tab
                    value="/home"
                    label="Access Review"
                    icon={<DashboardOutlinedIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{
                      minHeight: 42, fontSize: '0.78rem', fontWeight: 600,
                      color: 'rgba(255,255,255,0.55)',
                      textTransform: 'none', px: 2,
                      '&.Mui-selected': { color: '#fff' },
                    }}
                  />
                )}
                <Tab
                  value="/admin-dashboard"
                  label="Admin Dashboard"
                  icon={<AssessmentOutlinedIcon sx={{ fontSize: 16 }} />}
                  iconPosition="start"
                  sx={{
                    minHeight: 42, fontSize: '0.78rem', fontWeight: 600,
                    color: 'rgba(255,255,255,0.55)',
                    textTransform: 'none', px: 2,
                    '&.Mui-selected': { color: '#fff' },
                  }}
                />
              </Tabs>
            </Box>
          </Box>
        )}
      </AppBar>

      {/* ── Main Content ───────────────────────────────────────────────────── */}
      <Box
        component="main"
        sx={{ flex: 1, p: { xs: 2, md: 4 }, maxWidth: 1400, width: '100%', mx: 'auto' }}
      >
        {children}
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <Box
        component="footer"
        sx={{
          py: 2, px: 4,
          textAlign: 'center',
          bgcolor: '#fff',
          borderTop: '1px solid #E3E8EF',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          &copy; {new Date().getFullYear()} Yum! Brands, Inc. All Rights Reserved
        </Typography>
      </Box>
    </Box>
  );
}
